/**
 * Track the idle time of the user.
 * Timer is started when user logs in.
 * Check how long the user has been idle every minute.
 * Reset the idle time whenever user does something.
 */
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import storage from '@/utils/storage'
import { handleRedirectToAuth } from '@/hooks/useAuth'

const SESSION_TIMEOUT_MINS = storage.get('sc_timeout') || 60
const SESSION_CHECK_INTERVAL = 60 * 1000
const SESSION_TIMEOUT_MS = SESSION_TIMEOUT_MINS * 60 * 1000

export default function useSessionTimeoutTracker() {
  const router = useRouter()

  let idleTimeStart = new Date()
  let interval

  function timeSinceIdleInMS() {
    return new Date() - idleTimeStart
  }

  function msToMin(millis) {
    return Math.floor(millis / 1000 / 60)
  }

  function checkSessionTimeout(timeDeltaMS) {
    if (timeDeltaMS >= SESSION_TIMEOUT_MS) {
      clearInterval(interval)
      handleRedirectToAuth()
    }
  }

  function incrementTimer() {
    const timeDeltaMS = timeSinceIdleInMS()
    checkSessionTimeout(timeDeltaMS)
  }

  function resetIdleTime() {
    const timeDeltaMS = timeSinceIdleInMS()
    const timeDeltaMin = msToMin(timeDeltaMS)
    checkSessionTimeout(timeDeltaMS)
    if (timeDeltaMin !== 0) {
      idleTimeStart = new Date()
    }
  }

  useEffect(() => {
    if (router.isReady) {
      document.addEventListener('mousemove', resetIdleTime)
      document.addEventListener('mousedown', resetIdleTime)
      document.addEventListener('keypress', resetIdleTime)
      interval = setInterval(incrementTimer, SESSION_CHECK_INTERVAL)
    }

    return () => clearInterval(interval)
  }, [router?.isReady])
}
